import { probeServer } from './reachability';

const RECHECK_MS = 15_000;

/**
 * Tracks whether the device is online, for the offline banner and disabling actions.
 *
 * `navigator.onLine` going false is only a hint: Chrome takes it from the OS, which reports "no internet" with
 * VPNs, virtual network adapters or a blocked connectivity probe, and may never send `online` after sleep. So an
 * offline signal is confirmed against the server, and while offline the server is asked again until it answers.
 */
class NetworkState {
	online = $state(true);
	#pending: Promise<boolean> | null = null;
	#timer: ReturnType<typeof setInterval> | undefined;

	constructor() {
		if (typeof window === 'undefined') return;
		window.addEventListener('online', () => this.#set(true));
		window.addEventListener('offline', () => void this.check());
		window.addEventListener('focus', () => this.#recheck());
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') this.#recheck();
		});
		if (!navigator.onLine) void this.check();
	}

	/** Asks the server whether it can be reached and updates {@link online}; one probe at a time. */
	check(): Promise<boolean> {
		this.#pending ??= probeServer()
			.then((reachable) => {
				this.#set(reachable);
				return reachable;
			})
			.finally(() => (this.#pending = null));
		return this.#pending;
	}

	#recheck() {
		if (!this.online) void this.check();
	}

	#set(online: boolean) {
		this.online = online;
		clearInterval(this.#timer);
		this.#timer = online ? undefined : setInterval(() => void this.check(), RECHECK_MS);
	}
}

export const network = new NetworkState();
